import { stripLocalePrefix } from '@/lib/i18n/locale-path';
import { isPaywallBranchSlug } from '@/payment/attribution/checkout-analytics-context';
import type { PaymentProviderId } from '@/payment/provider-list';
import type { AdminV2PaywallPopupBranchSlug } from '@/site-runtime/code-owned/paywall-branches';

const SITE_RUNTIME_CHECKOUT_INTENT_VALUE = 'admin_v2_runtime';
const SITE_RUNTIME_CHECKOUT_INTENT_PARAMS = [
  'site_checkout_intent',
  'site_checkout_intent_key',
  'site_checkout_provider',
  'site_checkout_surface',
  'site_checkout_conversion_id',
  'site_checkout_paywall_branch',
] as const;
const CHECKOUT_INTENT_KEY_MAX_LENGTH = 200;
const CHECKOUT_CONVERSION_ID_PATTERN =
  /^cv_[0-9a-f]{8}-[0-9a-f]{4}-4[0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;

export type SiteRuntimeCheckoutSurface =
  | 'pricing_modal'
  | 'pricing_side_panel'
  | 'standalone_page'
  | 'upgrade_dialog';

export type SiteRuntimeCheckoutLoginIntent = {
  checkoutIntentKey: string;
  checkoutSurface: SiteRuntimeCheckoutSurface;
  conversionId: string;
  paywallBranchSlug?: AdminV2PaywallPopupBranchSlug;
  provider?: PaymentProviderId;
};

function createUrl(value: string) {
  try {
    return new URL(value, 'https://imgenhancer.local');
  } catch {
    return null;
  }
}

function toCallbackString(url: URL, originalValue: string) {
  if (originalValue.startsWith('/')) {
    return `${url.pathname}${url.search}${url.hash}`;
  }

  return url.toString();
}

function isPaymentProviderId(value: string | null): value is PaymentProviderId {
  return value === 'paypal' || value === 'stripe';
}

function isSiteRuntimeCheckoutSurface(
  value: string | null
): value is SiteRuntimeCheckoutSurface {
  return (
    value === 'pricing_modal' ||
    value === 'pricing_side_panel' ||
    value === 'standalone_page' ||
    value === 'upgrade_dialog'
  );
}

function normalizeCheckoutIntentKey(value: unknown): string | null {
  if (typeof value !== 'string') {
    return null;
  }
  const normalized = value.trim();
  if (!normalized || normalized.length > CHECKOUT_INTENT_KEY_MAX_LENGTH) {
    return null;
  }
  return normalized;
}

function normalizeConversionId(value: unknown): string | null {
  if (typeof value !== 'string') return null;
  const normalized = value.trim();
  return CHECKOUT_CONVERSION_ID_PATTERN.test(normalized) ? normalized : null;
}

export function stripSiteRuntimeCheckoutLoginIntent(callbackUrl: string) {
  const url = createUrl(callbackUrl);
  if (!url) {
    return callbackUrl;
  }

  for (const param of SITE_RUNTIME_CHECKOUT_INTENT_PARAMS) {
    url.searchParams.delete(param);
  }

  return toCallbackString(url, callbackUrl);
}

export function buildSiteRuntimeCheckoutLoginCallback(params: {
  callbackUrl: string;
  checkoutIntentKey: string;
  checkoutSurface: SiteRuntimeCheckoutSurface;
  conversionId: string;
  paywallBranchSlug?: AdminV2PaywallPopupBranchSlug;
  provider?: PaymentProviderId;
}) {
  const baseCallback = stripSiteRuntimeCheckoutLoginIntent(params.callbackUrl);
  const checkoutIntentKey = normalizeCheckoutIntentKey(
    params.checkoutIntentKey
  );
  const url = createUrl(baseCallback);
  if (!checkoutIntentKey || !url) {
    return baseCallback;
  }

  url.searchParams.set(
    'site_checkout_intent',
    SITE_RUNTIME_CHECKOUT_INTENT_VALUE
  );
  url.searchParams.set('site_checkout_intent_key', checkoutIntentKey);
  if (params.provider) {
    url.searchParams.set('site_checkout_provider', params.provider);
  }
  url.searchParams.set('site_checkout_surface', params.checkoutSurface);
  url.searchParams.set('site_checkout_conversion_id', params.conversionId);
  if (params.paywallBranchSlug) {
    url.searchParams.set(
      'site_checkout_paywall_branch',
      params.paywallBranchSlug
    );
  }

  return toCallbackString(url, baseCallback);
}

export function readSiteRuntimeCheckoutLoginIntent(
  href: string
): SiteRuntimeCheckoutLoginIntent | null {
  const url = createUrl(href);
  if (
    !url ||
    url.searchParams.get('site_checkout_intent') !==
      SITE_RUNTIME_CHECKOUT_INTENT_VALUE
  ) {
    return null;
  }

  const checkoutIntentKey = normalizeCheckoutIntentKey(
    url.searchParams.get('site_checkout_intent_key')
  );
  const providerValue = url.searchParams.get('site_checkout_provider');
  const checkoutSurface = url.searchParams.get('site_checkout_surface');
  const conversionId = normalizeConversionId(
    url.searchParams.get('site_checkout_conversion_id')
  );
  const paywallBranchValue = url.searchParams.get(
    'site_checkout_paywall_branch'
  );
  if (
    !checkoutIntentKey ||
    !conversionId ||
    (providerValue !== null && !isPaymentProviderId(providerValue)) ||
    (paywallBranchValue !== null && !isPaywallBranchSlug(paywallBranchValue)) ||
    !isSiteRuntimeCheckoutSurface(checkoutSurface)
  ) {
    return null;
  }

  return {
    checkoutIntentKey,
    checkoutSurface,
    conversionId,
    ...(paywallBranchValue ? { paywallBranchSlug: paywallBranchValue } : {}),
    ...(providerValue ? { provider: providerValue } : {}),
  };
}

export function buildSiteRuntimePaymentCallback(callbackUrl: string) {
  const callbackWithoutIntent =
    stripSiteRuntimeCheckoutLoginIntent(callbackUrl);
  if (
    !callbackWithoutIntent.startsWith('/') ||
    callbackWithoutIntent.startsWith('//')
  ) {
    return '/';
  }

  const url = createUrl(callbackWithoutIntent);
  if (!url) {
    return '/';
  }
  url.pathname = stripLocalePrefix(url.pathname);
  return toCallbackString(url, callbackWithoutIntent);
}

export function removeSiteRuntimeCheckoutLoginIntentFromBrowserUrl() {
  if (typeof window === 'undefined') {
    return;
  }

  const nextUrl = stripSiteRuntimeCheckoutLoginIntent(window.location.href);
  window.history.replaceState(window.history.state, '', nextUrl);
}
